import React, { useEffect, useState } from "react";
import { createCredencial, updateCredencial, getCredenciales } from "../services/api";
import { useNavigate, useParams } from "react-router-dom";
import CryptoJS from "crypto-js";

export default function CredencialForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const esEdicion = Boolean(id);

  // 🔐 CLAVE SECRETA (ideal mover a .env)
  const SECRET_KEY = "IP_DEPOT_SUPER_KEY_256!!##";

  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(esEdicion);

  const [form, setForm] = useState({
    servicio: "",
    usuario: "",
    contrasena: "",
    url: "",
    categoria: "",
    notas: "",
  });

  // ===========================
  // 🔄 CARGAR PARA EDICIÓN
  // ===========================
  useEffect(() => {
    if (esEdicion) cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const cargar = async () => {
    try {
      const res = await getCredenciales();
      const encontrada = (res.data || []).find((c) => c.id === id);

      if (!encontrada) {
        setError("Credencial no encontrada.");
        return;
      }

      setForm({
        servicio: encontrada.servicio || "",
        usuario: encontrada.usuario || "",
        contrasena: "", // 🔐 nunca mostramos la real
        url: encontrada.url || "",
        categoria: encontrada.categoria || "",
        notas: encontrada.notas || "",
      });
    } catch (err) {
      console.error(err);
      setError("Error cargando la credencial.");
    } finally {
      setLoading(false);
    }
  };

  // ===========================
  // 🔐 CIFRADO
  // ===========================
  const encrypt = (value) =>
    CryptoJS.AES.encrypt(value, SECRET_KEY).toString();

  // ===========================
  // ✏️ CAMBIOS EN FORM
  // ===========================
  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  // ===========================
  // ✅ GUARDAR (CREAR / EDITAR)
  // ===========================
  const guardar = async () => {
    if (!form.servicio.trim() || !form.usuario.trim()) {
      setError("Servicio y usuario son obligatorios.");
      return;
    }

    try {
      const payload = {
        servicio: form.servicio,
        usuario: form.usuario,
        url: form.url,
        categoria: form.categoria,
        notas: form.notas,
      };

      // 🔐 solo cifra si se escribió algo nuevo
      if (form.contrasena) {
        payload.contrasena = encrypt(form.contrasena);
      }

      if (esEdicion) {
        await updateCredencial(id, payload);
      } else {
        if (!form.contrasena) {
          setError("La contraseña es obligatoria.");
          return;
        }
        payload.contrasena = encrypt(form.contrasena);
        await createCredencial(payload);
      }

      navigate("/sitios");
    } catch (err) {
      console.error(err);
      setError("Error al guardar credencial.");
    }
  };

  if (loading) {
    return <div className="p-10 text-center text-slate-500">Cargando…</div>;
  }

  return (
    <div className="min-h-screen bg-slate-100 flex justify-center py-10 px-4">
      <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-3xl border border-slate-200">

        {/* TÍTULO */}
        <h1 className="text-3xl font-extrabold text-slate-900 mb-2">
          {esEdicion ? "Editar credencial" : "Nueva credencial"}
        </h1>

        <p className="text-slate-500 text-sm mb-6">
          {esEdicion
            ? "Actualiza la información del acceso. La contraseña es opcional."
            : "Registra un nuevo acceso de forma segura."}
        </p>

        {/* ERROR */}
        {error && (
          <div className="bg-red-100 text-red-700 text-sm p-3 rounded-lg mb-4 border border-red-300">
            {error}
          </div>
        )}

        {/* FORM */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

          {/* SERVICIO */}
          <div>
            <label className="text-sm font-medium text-slate-700">Servicio *</label>
            <input
              name="servicio"
              value={form.servicio}
              onChange={handleChange}
              className="mt-1 w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
              placeholder="Ej. AWS, Gmail"
            />
          </div>

          {/* USUARIO */}
          <div>
            <label className="text-sm font-medium text-slate-700">Usuario *</label>
            <input
              name="usuario"
              value={form.usuario}
              onChange={handleChange}
              className="mt-1 w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
              placeholder="correo@ejemplo.com"
            />
          </div>
        </div>

        {/* CONTRASEÑA */}
        <div className="mt-4">
          <label className="text-sm font-medium text-slate-700">
            {esEdicion ? "Nueva contraseña (opcional)" : "Contraseña *"}
          </label>

          <div className="relative">
            <input
              type={showPass ? "text" : "password"}
              name="contrasena"
              value={form.contrasena}
              onChange={handleChange}
              className="mt-1 w-full border rounded-lg px-3 py-2 pr-12 focus:ring-2 focus:ring-indigo-500"
              placeholder={esEdicion ? "•••••••• (opcional)" : "••••••••"}
            />

            <button
              type="button"
              onClick={() => setShowPass(!showPass)}
              className="absolute right-3 top-3 text-slate-500 hover:text-slate-700"
            >
              {showPass ? "🙈" : "👁️"}
            </button>
          </div>
        </div>

        {/* URL */}
        <div className="mt-4">
          <label className="text-sm font-medium text-slate-700">URL</label>
          <input
            name="url"
            value={form.url}
            onChange={handleChange}
            className="mt-1 w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
            placeholder="https://..."
          />
        </div>

        {/* CATEGORÍA */}
        <div className="mt-4">
          <label className="text-sm font-medium text-slate-700">Categoría</label>
          <input
            name="categoria"
            value={form.categoria}
            onChange={handleChange}
            className="mt-1 w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
            placeholder="Ej. Gobierno, SaaS"
          />
        </div>

        {/* NOTAS */}
        <div className="mt-4">
          <label className="text-sm font-medium text-slate-700">Notas</label>
          <textarea
            name="notas"
            rows="3"
            value={form.notas}
            onChange={handleChange}
            className="mt-1 w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
            placeholder="Información adicional…"
          />
        </div>

        {/* BOTONES */}
        <div className="flex gap-3 mt-6">
          <button
            onClick={guardar}
            className="bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-indigo-700 transition"
          >
            Guardar
          </button>

          <button
            onClick={() => navigate("/sitios")}
            className="bg-slate-200 text-slate-700 px-6 py-3 rounded-lg font-medium hover:bg-slate-300 transition"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}
